// dateUtils.ts
import {
    differenceInCalendarDays,
    endOfISOWeek,
    endOfMonth,
    format,
    getISOWeek,
    parseISO,
    startOfISOWeek,
    startOfMonth,
    subDays,
} from 'date-fns';
import type { AggregateRow, DateRange, PeriodMeta, PeriodType, SkuPivotRow, SkuVarianceApiRow, WeekMeta } from './types';

export const defaultLast4Weeks = (): DateRange => {
    const days = 28;
    const end = new Date();
    const start = subDays(end, days - 1);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
    };
};

export const defaultCurrentWeek = (): DateRange => {
    const today = new Date();
    const start = startOfISOWeek(today);
    const end = endOfISOWeek(today);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
    };
};

export const defaultCurrentMonth = (): DateRange => {
    const today = new Date();
    const start = startOfMonth(today);
    const end = endOfMonth(today);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
    };
};

// Returns the appropriate default range based on period type
export const defaultCurrentPeriod = (periodType: PeriodType): DateRange => {
    if (periodType === 'MONTH') {
        return defaultCurrentMonth();
    }
    return defaultCurrentWeek();
};

// Normalize backend date key (dd-mm-yyyy -> yyyy-mm-dd) so Date parsing works
export const normalizeDateKey = (key: string): string => {
    if (!key) return key;
    const match = key.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (match) {
        const [, day, month, year] = match;
        return `${year}-${month}-${day}`;
    }
    return key;
};

export const formatDisplayRange = (range: DateRange): string => {
    if (!range.startDate || !range.endDate) return '';
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);

    const sameMonth =
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear();

    if (sameMonth) {
        return `${format(start, 'MMM dd')} - ${format(end, 'dd')}`;
    }

    return `${format(start, 'MMM dd')} - ${format(end, 'MMM dd')}`;
};

// current range -> previous same-length range
export const buildPreviousRange = (range: DateRange): DateRange => {
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const days = differenceInCalendarDays(end, start) + 1;

    const prevEnd = subDays(start, 1);
    const prevStart = subDays(prevEnd, days - 1);

    return {
        startDate: format(prevStart, 'yyyy-MM-dd'),
        endDate: format(prevEnd, 'yyyy-MM-dd'),
    };
};

// Forecast accuracy from totals
export const computeForecastAccuracy = (
    totalSales: number,
    totalForecast: number,
): string => {
    if (!totalSales && !totalForecast) return '—';
    if (!totalForecast && totalSales) return '0%';

    const raw =
        (1 -
            Math.abs(totalSales - totalForecast) / Math.max(totalForecast, 1)) *
        100;

    const clamped = Math.max(0, Math.min(100, Math.round(raw)));
    return `${clamped}%`;
};

// Growth label from current vs previous sales
export const computeGrowthLabel = (
    current: number,
    previous: number,
): string => {
    if (!previous && !current) return '—';
    if (!previous && current) return '—';

    const raw = ((current - previous) / Math.max(previous, 1)) * 100; // %
    const value = Math.round(raw * 10) / 10; // 1 decimal
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}%`;
};

// Date string -> week meta
export const getWeekKeyMeta = (dateKey: string): WeekMeta => {
    try {
        const d = parseISO(dateKey);
        if (Number.isNaN(d.getTime())) throw new Error('invalid');

        const week = getISOWeek(d);
        const year = d.getFullYear();
        const start = startOfISOWeek(d);
        const end = endOfISOWeek(d);
        const startLabel = format(start, 'dd MMM');
        const endLabel =
            start.getFullYear() === end.getFullYear()
                ? format(end, 'dd MMM')
                : format(end, 'dd MMM yyyy');

        return {
            key: `${year}-W${week}`,
            label: `${startLabel} - ${endLabel}`,
            startDate: format(start, 'yyyy-MM-dd'),
            endDate: format(end, 'yyyy-MM-dd'),
            /* order: year * 100 + week, */
            order: d.getTime(),  // Use actual timestamp
        };
    } catch {
        return {
            key: dateKey,
            label: dateKey,
            startDate: dateKey,
            endDate: dateKey,
            order: Number.MAX_SAFE_INTEGER,
        };
    }
};

// Date string -> month meta
export const getMonthKeyMeta = (dateKey: string): PeriodMeta => {
    try {
        const d = parseISO(dateKey);
        if (Number.isNaN(d.getTime())) throw new Error('invalid');

        const month = d.getMonth(); // 0-11
        const year = d.getFullYear();
        const start = startOfMonth(d);
        const end = endOfMonth(d);

        return {
            key: `${year}-${String(month + 1).padStart(2, '0')}`, // e.g., "2026-01"
            label: format(d, 'MMM yyyy'), // e.g., "Jan 2026"
            startDate: format(start, 'yyyy-MM-dd'),
            endDate: format(end, 'yyyy-MM-dd'),
            order: d.getTime(),
        };
    } catch {
        return {
            key: dateKey,
            label: dateKey,
            startDate: dateKey,
            endDate: dateKey,
            order: Number.MAX_SAFE_INTEGER,
        };
    }
};

// Generic period meta based on period type
export const getPeriodKeyMeta = (dateKey: string, periodType: PeriodType): PeriodMeta => {
    if (periodType === 'MONTH') {
        return getMonthKeyMeta(dateKey);
    }
    return getWeekKeyMeta(dateKey);
};

// Build periods list + SKU pivot rows for table (generic for weeks/months)
export const buildPeriodPivot = (
    salesRows: AggregateRow[],
    forecastRows: AggregateRow[],
    periodType: PeriodType = 'WEEK',
): { periods: PeriodMeta[]; rows: SkuPivotRow[] } => {
    const periodMap = new Map<string, PeriodMeta>();
    const skuMap = new Map<string, SkuPivotRow>();

    const upsertPeriod = (dateKey: string): PeriodMeta => {
        const meta = getPeriodKeyMeta(dateKey, periodType);
        if (!periodMap.has(meta.key)) periodMap.set(meta.key, meta);
        return periodMap.get(meta.key)!;
    };

    const upsertSkuRow = (sku: string, name: string): SkuPivotRow => {
        const key = sku || name;
        const existing = skuMap.get(key);
        if (existing) return existing;
        const row: SkuPivotRow = {
            sku: sku || name || '-',
            name: name || sku || '-',
            metricsByPeriod: {},
        };
        skuMap.set(key, row);
        return row;
    };

    const ingest = (rows: AggregateRow[], source: 'sales' | 'forecast') => {
        rows.forEach((row) => {
            if (!row.grouping) return;
            const { DATE, PRODUCT } = row.grouping;
            if (!PRODUCT || !DATE) return;

            const period = upsertPeriod(normalizeDateKey(DATE.key));
            const skuRow = upsertSkuRow(PRODUCT.sku, PRODUCT.name);
            const bucket = skuRow.metricsByPeriod[period.key] || {};

            if (source === 'sales') {
                bucket.actual = (bucket.actual || 0) + (row.data.quantity || 0);
            } else {
                bucket.forecast =
                    (bucket.forecast || 0) + (row.data.quantity || 0);
            }

            skuRow.metricsByPeriod[period.key] = bucket;
        });
    };

    ingest(salesRows, 'sales');
    ingest(forecastRows, 'forecast');

    // Sort ascending (chronological: oldest first)
    const periods = Array.from(periodMap.values()).sort(
        (a, b) => a.order - b.order,
    );
    const rows = Array.from(skuMap.values()).sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
    );

    return { periods, rows };
};

// Build periods + SKU pivot rows from the /forecasts/sku-variance API response
export const buildSkuVariancePivot = (
    apiRows: SkuVarianceApiRow[],
): { periods: PeriodMeta[]; rows: SkuPivotRow[] } => {
    const periodMap = new Map<string, PeriodMeta>();
    const pivotRows: SkuPivotRow[] = [];

    for (const apiRow of apiRows) {
        const row: SkuPivotRow = {
            sku: apiRow.sku || apiRow.name || '-',
            name: apiRow.name || apiRow.sku || '-',
            metricsByPeriod: {},
        };

        for (const period of apiRow.periods) {
            const normalizedKey = normalizeDateKey(period.periodKey);
            const meta = getPeriodKeyMeta(normalizedKey, period.periodType as PeriodType);

            if (!periodMap.has(meta.key)) {
                periodMap.set(meta.key, meta);
            }

            row.metricsByPeriod[meta.key] = {
                forecast: period.forecast,
                actual: period.actual,
                variance: period.variance,
            };
        }

        pivotRows.push(row);
    }

    // Sort periods ascending (chronological: Jan, Feb, Mar)
    const periods = Array.from(periodMap.values()).sort((a, b) => a.order - b.order);
    const rows = pivotRows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return { periods, rows };
};

// Backward compatible wrapper - builds weeks list + SKU pivot rows for table
export const buildWeekPivot = (
    salesRows: AggregateRow[],
    forecastRows: AggregateRow[],
): { weeks: WeekMeta[]; rows: SkuPivotRow[] } => {
    const { periods, rows } = buildPeriodPivot(salesRows, forecastRows, 'WEEK');
    
    // Add metricsByWeek alias for backward compatibility
    rows.forEach(row => {
        row.metricsByWeek = row.metricsByPeriod;
    });
    
    return { weeks: periods, rows };
};
